import React, { useCallback, useContext } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { StandardGrid, GridElement } from '../../Grid';
import { getStreamItemId } from '../../Duperbrowse/redux/helpers';
import type { PapiDeviation } from '@wix/da-papi-types';
import StandardThumb from '../../DeviationViews/Thumb/Standard';
import MobileThumb, {
  EXTRAS_HEIGHT as MOBILE_EXTRAS_HEIGHT,
} from '../../DeviationViews/Thumb/Mobile';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';

export interface Props {
  items: PapiDeviation[];
  cookieType?: MeasuredCookieType;
  enableScrollOptim?: boolean;
  contentStrip?: React.ReactNode;
  contentStripIndex?: number;
  contentStripHeight?: number;
  adjustHeight?: number;
  spacing?: number;
  allowMobileCard?: boolean;
  // Inject ad with Nth row
  adAfterNRow?: number;
  adRenderer?: (rowId: number) => React.ReactNode;
}

const DeviationGrid: React.FC<Props> = ({
  items,
  adjustHeight,
  cookieType,
  contentStrip,
  contentStripIndex,
  contentStripHeight,
  enableScrollOptim,
  spacing,
  allowMobileCard: allowMobileCardProp,
  adAfterNRow,
  adRenderer,
}) => {
  const isMobile = useContext(MobileContext);
  const allowMobileCard = allowMobileCardProp && isMobile;

  const getGridItemId = useCallback(
    (index: number) => {
      const item = items[index];
      return `item-${item.typeId}-${item.deviationId}`;
    },
    [items]
  );

  const getDuperbrowseItemId = useCallback(
    (index: number) => {
      return getStreamItemId(items[index]);
    },
    [items]
  );

  const renderGridItem = useCallback(
    (element: GridElement) => {
      const { index, width, height, style } = element;
      const item = items[index];
      if (!item) {
        return;
      }

      if (allowMobileCard) {
        return (
          <MobileThumb
            key={getGridItemId(index)}
            deviation={item}
            width={width}
            height={height}
            style={style}
          />
        );
      }

      return (
        <StandardThumb
          key={getGridItemId(index)}
          deviation={item}
          width={width}
          height={height}
          style={style}
        />
      );
    },
    [getGridItemId, items, allowMobileCard]
  );

  return (
    <StandardGrid
      contentStrip={contentStrip}
      contentStripIndex={contentStripIndex}
      contentStripHeight={contentStripHeight}
      elementCount={items.length}
      cookieType={cookieType}
      getElementId={getGridItemId}
      getDuperbrowseItemId={getDuperbrowseItemId}
      adjustHeight={allowMobileCard ? MOBILE_EXTRAS_HEIGHT + (adjustHeight ?? 0) : adjustHeight}
      enableScrollOptim={enableScrollOptim}
      spacing={spacing}
      adAfterNRow={adAfterNRow}
      adRenderer={adRenderer}
    >
      {renderGridItem}
    </StandardGrid>
  );
};

DeviationGrid.displayName = 'DeviationGrid';

export default DeviationGrid;
